import { useContext, useEffect, useRef, useState } from "react";
import AuthContext from "../context/auth/AuthContext";
import useTranslator from "../hooks/useTranslator";
import { Swiper, SwiperSlide } from "swiper/react";
import { haveValue, placeNotificationLink, toast } from "../helper/common";
import LandLordContext from "../context/landlord/LandLordContext";
import { useNavigate } from "react-router-dom";
import { calculateNowTime } from "../services/sharedService";
import { Scrollbar, Mousewheel } from "swiper/modules";
import SkorDefaultLoader from "../assets/img/skorDefaultLoader.gif";

const WorkspaceDashboard = () => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const loadingRef = useRef(false);
  const { toggleNewWorkspaceModal, notificationSeen, user, loadUserTenant } =
    authContext;

  const landlordContext = useContext(LandLordContext);
  const { fetchAllLandlordNotification, fetchWorkspacesOverview } =
    landlordContext;

  const [groupedNotifications, setGroupedNotifications] = useState({});
  const [unreadCount, setUnreadCount] = useState(0);
  const [allWorkspaces, setAllWorkspaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [allNotifications, setAllNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (allNotifications?.length > 0) {
      const groupedData = groupNotificationsByDate(allNotifications);
      setGroupedNotifications(groupedData);
    }
  }, [allNotifications]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + window.scrollY >=
          document.body.offsetHeight - 500 &&
        !loadingRef.current &&
        allNotifications.length < total
      ) {
        loadData(page + 1);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [page, total]);

  const groupNotificationsByDate = (notifications) => {
    return notifications.reduce((acc, item) => {
      const date = new Date(item.createdAt);

      const day = String(date.getDate()).padStart(2, "0");
      const month = date.toLocaleString("en-GB", { month: "long" });
      const year = date.getFullYear();

      const dateKey = `${day} ${month}, ${year}`;

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }

      acc[dateKey].push(item);
      return acc;
    }, {});
  };

  const loadData = async (currentPage) => {
    try {
      setLoading(true);
      if (loadingRef.current) return;
      loadingRef.current = true;

      const notificationRes = await fetchAllLandlordNotification(currentPage);

      setAllNotifications((prev) => [
        ...prev,
        ...(notificationRes?.notifications || []),
      ]);
      setUnreadCount(notificationRes?.unread_notifications || 0);
      setTotal(notificationRes?.total_notifications);
      setPage(notificationRes?.current_page);
    } catch (err) {
      console.log(err, "");
      toast.error(_t("errorWhileFetchingDashboardNotifications"));
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    loadData();
    Promise.all([fetchWorkspacesOverview()])
      .then(([workspaceRes]) => {
        setAllWorkspaces(workspaceRes?.success_res);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [user]);

  useEffect(() => {
    if (swiperInstance && allWorkspaces?.length > 0) {
      swiperInstance.update();
    }
  }, [allWorkspaces, swiperInstance]);

  return (
    <div className="workspace-dashboard-main">
      {/* ALL WORKSPACES */}
      <div className="all-workspace-container">
        <div className="d-flex align-items-center justify-content-between add-workspace-wrapper">
          <div className="d-flex align-items-center custom-gap-3">
            <div className="all-workspace-icon-wrapper d-flex align-items-center justify-content-center">
              <img src="/images/briefcaseIcon.svg" alt="Icon" />
            </div>
            <div className="all-workspace-text-wrapper">
              <h4>{_t("myWorkspaces")}</h4>
              <p>
                {user?.workspaceIds?.length} {_t("workspaces")}
              </p>
            </div>
          </div>
          <button
            className="addNewWorkspaceButton d-flex align-items-center custom-gap-2"
            onClick={() => toggleNewWorkspaceModal(true)}
          >
            <img src="/images/add-white.svg" alt="" />
            {_t("addNewWorkspace")}
          </button>
        </div>

        <div className="d-flex flex-nowrap workspace-info-card-wrapper">
          <Swiper
            key={allWorkspaces.length}
            slidesPerView={1.4}
            spaceBetween={18}
            grabCursor={allWorkspaces.length > 1}
            className="mySwiper"
            observer={true}
            observeSlideChildren={true}
            observeParents={true}
            updateOnImagesReady={true}
            modules={[Mousewheel, Scrollbar]}
            scrollbar={{
              draggable: true,
              hide: false,
            }}
            freeMode={true}
            mousewheel={{
              forceToAxis: true,
              sensitivity: 2.5,
              releaseOnEdges: false,
              thresholdDelta: 1,
            }}
            onSwiper={setSwiperInstance}
            breakpoints={{
              1280: { slidesPerView: 5.7 },
            }}
          >
            {allWorkspaces?.map((workspace) => (
              <SwiperSlide key={workspace?._id}>
                <div
                  className="workspace-info-card"
                  role="button"
                  onClick={() => {
                    loadUserTenant("landlord");
                    navigate(`/${workspace?.uuid}/profile-request`);
                  }}
                >
                  <div className="time-container d-flex align-items-start justify-content-end">
                    {workspace?.lastOpenedAt && (
                      <p>
                        {calculateNowTime(
                          workspace?.lastOpenedAt,
                          localStorage.getItem("i18nextLng")
                        )}
                      </p>
                    )}
                  </div>
                  <div
                    className={`workspace-image-container ${
                      !haveValue(workspace?.workspaceImage) ? "no-image" : ""
                    }`}
                  >
                    {haveValue(workspace?.workspaceImage) ? (
                      <img
                        src={`${process.env.REACT_APP_CDN_ENDPOINT}/${process.env.REACT_APP_CDN_CONTAINER}/${workspace?.workspaceImage}`}
                        alt=""
                      />
                    ) : (
                      <h4>{workspace.name?.substring(0, 1)}</h4>
                    )}
                  </div>
                  <h5 className="two-line-ellipsis">{workspace?.name}</h5>
                  <p>
                    {workspace?.memberCount} {_t("members")}
                  </p>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      {/* NOTIFICATION CONTAINER */}
      <div className="all-workspace-container notification-container">
        <div className="d-flex align-items-center justify-content-between add-workspace-wrapper">
          <div className="d-flex align-items-center custom-gap-3">
            <div className="all-workspace-icon-wrapper d-flex align-items-center justify-content-center">
              <img src="/images/bell-icon-black.svg" alt="Icon" />
            </div>
            <div className="all-workspace-text-wrapper">
              <h4>{_t("notifications")}</h4>
            </div>
          </div>
          <p className="unreadText">
            {unreadCount} {_t("unread")}
          </p>
        </div>
        {Object.keys(groupedNotifications).length > 0
          ? Object.entries(groupedNotifications)?.map(
              ([date, notifications], index, arr) => (
                <div
                  key={index}
                  className="notification-wrapper-main d-flex custom-gap-3"
                >
                  <div
                    className={`notification-info-time ${
                      index === arr.length - 1 ? "last-child" : ""
                    }`}
                  >
                    <h5>{date}</h5>
                    <p>
                      {notifications.filter((n) => !n.marked_read).length}{" "}
                      {_t("unread")}
                    </p>
                  </div>
                  <div className="d-flex flex-column notification-info-main">
                    {notifications.map((notification) => (
                      <div
                        className={`notification-info ${
                          !notification?.marked_read ? "unread" : "read"
                        }`}
                        role="button"
                        onClick={() => {
                          notificationSeen(notification?._id);
                          navigate(
                            placeNotificationLink(
                              notification,
                              notification?.workspaceId?.uuid
                            )
                          );
                        }}
                      >
                        <h6>
                          {(() => {
                            switch (notification?.notification_type) {
                              case "guarantor_removed":
                                const splited = notification?.body.split(" ");
                                return `${splited[0]} ${splited[1]} ${_t(
                                  "hasRemovedHimselfAsAGuarantorFor"
                                )} ${
                                  notification?.shared_profile_id?.tenant_fname
                                } ${
                                  notification?.shared_profile_id?.tenant_lname
                                }`;

                              case "guarantor_declined":
                                return `${_t("guarantorRequestDeclinedBy")} ${
                                  notification?.created_by_tenant?.fname
                                } ${notification?.created_by_tenant?.lname}`;

                              case "property_assigned":
                                return `${
                                  notification?.created_by_landlord
                                    ?.fname_companyName
                                } ${_t("hasAwardedYouAProperty")}`;

                              case "profile_linked":
                                return `${
                                  notification?.created_by_landlord
                                    ?.fname_companyName
                                } ${_t("hasLinkedYourProfileToAAddress")}.`;

                              case "profile_sharing":
                                return `${
                                  notification?.created_by_tenant?.fname
                                } ${_t("hasSharedProfileWithYou")}`;

                              case "document_updated":
                                return `${
                                  notification?.created_by_tenant?.fname
                                } ${
                                  notification?.created_by_tenant?.lname
                                } ${_t("hasUpdatedTheirDocuments")}`;

                              case "guarantor_document_updated":
                                return `${
                                  notification?.created_by_tenant?.fname
                                } ${
                                  notification?.created_by_tenant?.lname
                                }'s ${_t("guarantor")} ${_t(
                                  "hasUpdatedTheirDocuments"
                                )}`;

                              case "guarantor_added":
                                return `${
                                  notification?.created_by_tenant?.fname
                                } ${
                                  notification?.created_by_tenant?.lname
                                } ${_t("hasAddedNewGuarantor")}`;

                              case "financial_info_updated":
                                return `${
                                  notification?.created_by_tenant?.fname
                                } ${
                                  notification?.created_by_tenant?.lname
                                } ${_t("hasUpdatedTheirFinancialInformation")}`;

                              case "financial_info_updated_guarantor":
                                return `${
                                  notification?.created_by_tenant?.fname
                                } ${
                                  notification?.created_by_tenant?.lname
                                }'s guarantor ${_t(
                                  "hasUpdatedTheirFinancialInformation"
                                )}`;
                            }
                          })(notification)}
                        </h6>
                        <p>
                          {calculateNowTime(
                            notification?.createdAt,
                            localStorage.getItem("i18nextLng")
                          )}
                        </p>
                        {(notification?.workspaceId?.workspaceImage ||
                          notification?.workspaceId?.name) && (
                          <div className="d-flex align-items-center workspace-name-wrapper custom-gap-2">
                            {haveValue(
                              notification?.workspaceId?.workspaceImage
                            ) ? (
                              <img
                                src={`${process.env.REACT_APP_CDN_ENDPOINT}/${process.env.REACT_APP_CDN_CONTAINER}/${notification?.workspaceId?.workspaceImage}`}
                                alt=""
                              />
                            ) : (
                              notification?.workspaceId?.name && (
                                <h5 className="d-flex align-items-center justify-content-center">
                                  {notification?.workspaceId?.name?.substring(
                                    0,
                                    1
                                  )}
                                </h5>
                              )
                            )}
                            <h4>{notification?.workspaceId?.name}</h4>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )
            )
          : !loading && <div className="noData">{_t("noDataFound")}</div>}
        {loading && (
          <div className="defaultSkorLoaderParent mt-3">
            <img
              src={SkorDefaultLoader}
              className="defaultSkorLoaderSmall"
              alt="Example"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkspaceDashboard;
